/**
 * Helpers pour les équipements et types de lit
 * Mapping icônes et labels pour l'affichage
 */

import type { RoomEquipment, BedType, EquipmentCategory } from '~/utils/constants'
import { EQUIPMENT_BY_CATEGORY } from '#shared/room-constants'

// Mapping des équipements avec icônes Lucide et labels français
export const EQUIPMENT_CONFIG: Record<RoomEquipment, { icon: string, label: string }> = {
  // Chambre
  linge_de_lit: { icon: 'i-lucide-shirt', label: 'Linge de lit fourni' },
  climatisation: { icon: 'i-lucide-wind', label: 'Climatisation' },
  coin_salon: { icon: 'i-lucide-armchair', label: 'Coin salon' },
  tv: { icon: 'i-lucide-tv', label: 'Télévision' },
  vod: { icon: 'i-lucide-play', label: 'VOD / Streaming' },
  armoire: { icon: 'i-lucide-archive', label: 'Armoire / penderie' },
  bureau: { icon: 'i-lucide-briefcase', label: 'Bureau' },
  coffre: { icon: 'i-lucide-lock', label: 'Coffre-fort' },
  spa_prive: { icon: 'i-lucide-sparkles', label: 'Spa privé' },
  lit_bebe_equip: { icon: 'i-lucide-baby', label: 'Lit bébé équipé' },
  chaise_haute: { icon: 'i-lucide-armchair', label: 'Chaise haute' },
  baignoire_bebe: { icon: 'i-lucide-bath', label: 'Baignoire bébé' },
  table_langer: { icon: 'i-lucide-table', label: 'Table à langer' },
  // Salle de bains
  douche: { icon: 'i-lucide-shower-head', label: 'Douche' },
  douche_italienne: { icon: 'i-lucide-shower-head', label: 'Douche italienne' },
  baignoire: { icon: 'i-lucide-bath', label: 'Baignoire' },
  baignoire_balneo: { icon: 'i-lucide-bath', label: 'Baignoire balnéo' },
  lavabo: { icon: 'i-lucide-droplets', label: 'Lavabo' },
  vasque: { icon: 'i-lucide-droplets', label: 'Vasque' },
  seche_cheveux: { icon: 'i-lucide-wind', label: 'Sèche-cheveux' },
  seche_serviettes: { icon: 'i-lucide-heater', label: 'Sèche-serviettes' },
  linge_toilette: { icon: 'i-lucide-shirt', label: 'Linge de toilette' },
  peignoirs: { icon: 'i-lucide-shirt', label: 'Peignoirs' },
  savon_shampoing: { icon: 'i-lucide-pipette', label: 'Savon & shampooing' },
  // WC
  wc_prive: { icon: 'i-lucide-door-closed', label: 'WC privé' },
  wc_partage: { icon: 'i-lucide-door-open', label: 'WC partagé' },
  toilette_seche: { icon: 'i-lucide-leaf', label: 'Toilette sèche' },
  // Boissons chaudes (catégorie technique `cuisine`)
  plateau_courtoisie: { icon: 'i-lucide-coffee', label: 'Plateau de courtoisie' },
  mini_refrigerateur: { icon: 'i-lucide-refrigerator', label: 'Mini-réfrigérateur' },
  machine_cafe: { icon: 'i-lucide-coffee', label: 'Machine à café' },
  bouilloire: { icon: 'i-lucide-cup-soda', label: 'Bouilloire' },
  the_tisane: { icon: 'i-lucide-leaf', label: 'Thé / Tisane' },
  // Extérieur
  terrasse_partagee: { icon: 'i-lucide-sun', label: 'Terrasse partagée' },
  terrasse: { icon: 'i-lucide-sun', label: 'Terrasse' },
  terrasse_privee: { icon: 'i-lucide-sun', label: 'Terrasse privée' },
  balcon: { icon: 'i-lucide-fence', label: 'Balcon' },
  mobilier_jardin: { icon: 'i-lucide-tree-palm', label: 'Mobilier de jardin' },
  transats: { icon: 'i-lucide-recliner', label: 'Transats' },
  barbecue: { icon: 'i-lucide-flame', label: 'Barbecue' },
  // Vues
  vue_jardin: { icon: 'i-lucide-flower-2', label: 'Vue jardin' },
  vue_piscine: { icon: 'i-lucide-waves', label: 'Vue piscine' },
  vue_campagne: { icon: 'i-lucide-wheat', label: 'Vue campagne' },
  vue_montagne: { icon: 'i-lucide-mountain', label: 'Vue montagne' },
  vue_mer: { icon: 'i-lucide-waves', label: 'Vue mer' },
  vue_lac: { icon: 'i-lucide-waves', label: 'Vue lac' },
  vue_riviere: { icon: 'i-lucide-waves', label: 'Vue rivière' },
  vue_foret: { icon: 'i-lucide-trees', label: 'Vue forêt' },
  vue_rue: { icon: 'i-lucide-building', label: 'Vue rue' },
  vue_ville: { icon: 'i-lucide-building-2', label: 'Vue ville' },
  vue_cour: { icon: 'i-lucide-home', label: 'Vue cour' },
  vue_parc: { icon: 'i-lucide-tree-deciduous', label: 'Vue parc' },
}

// Mapping des types de lit avec icônes Lucide et labels français
export const BED_TYPE_CONFIG: Record<BedType, { icon: string, label: string }> = {
  SINGLE: { icon: 'i-lucide-bed-single', label: 'Lit simple' },
  DOUBLE: { icon: 'i-lucide-bed-double', label: 'Lit double' },
  QUEEN: { icon: 'i-lucide-bed-double', label: 'Lit queen' },
  KING: { icon: 'i-lucide-bed-double', label: 'Lit king-size' },
  BUNK_2: { icon: 'i-lucide-bed', label: 'Lits superposés' },
  SOFA_BED: { icon: 'i-lucide-sofa', label: 'Canapé-lit' },
  FUTON_BED: { icon: 'i-lucide-bed', label: 'Futon' },
  TRUNDLE: { icon: 'i-lucide-bed', label: 'Lit gigogne' },
  BABY_CRIB: { icon: 'i-lucide-baby', label: 'Lit bébé' },
  CHILD_BED: { icon: 'i-lucide-bed-single', label: 'Lit enfant' },
  EXTRA_BED: { icon: 'i-lucide-bed-single', label: 'Lit d\'appoint' },
}

// Labels des catégories d'équipements pour l'affichage UI
export const EQUIPMENT_CATEGORY_LABELS: Record<EquipmentCategory, { label: string, icon: string }> = {
  chambre: { label: 'Chambre', icon: 'i-lucide-bed-double' },
  salle_de_bains: { label: 'Salle de bains', icon: 'i-lucide-shower-head' },
  wc: { label: 'WC', icon: 'i-lucide-door-closed' },
  cuisine: { label: 'Boissons', icon: 'i-lucide-coffee' },
  exterieur: { label: 'Extérieur', icon: 'i-lucide-sun' },
  vues: { label: 'Vues', icon: 'i-lucide-eye' },
}

// Catégories d'équipements pour l'affichage groupé (structure enrichie)
export const EQUIPMENT_CATEGORIES = EQUIPMENT_BY_CATEGORY

/**
 * Retourne l'icône d'un équipement
 */
export function getEquipmentIcon(equipment: string): string {
  return EQUIPMENT_CONFIG[equipment as RoomEquipment]?.icon ?? 'i-lucide-box'
}

/**
 * Retourne le label français d'un équipement
 */
export function getEquipmentLabel(equipment: string): string {
  return EQUIPMENT_CONFIG[equipment as RoomEquipment]?.label ?? equipment
}

/**
 * Retourne l'icône d'un type de lit
 */
export function getBedTypeIcon(bedType: string): string {
  return BED_TYPE_CONFIG[bedType as BedType]?.icon ?? 'i-lucide-bed'
}

/**
 * Retourne le label français d'un type de lit
 */
export function getBedTypeLabel(bedType: string): string {
  return BED_TYPE_CONFIG[bedType as BedType]?.label ?? bedType
}

/**
 * Retourne les équipements groupés par catégorie (filtré par ceux sélectionnés)
 */
export function getEquipmentsByCategory(equipments: RoomEquipment[]) {
  return (Object.entries(EQUIPMENT_BY_CATEGORY) as [EquipmentCategory, readonly RoomEquipment[]][]).map(([key, items]) => ({
    key,
    label: EQUIPMENT_CATEGORY_LABELS[key].label,
    icon: EQUIPMENT_CATEGORY_LABELS[key].icon,
    items: items
      .filter(item => equipments.includes(item))
      .map(item => ({
        value: item,
        ...EQUIPMENT_CONFIG[item],
      })),
  })).filter(cat => cat.items.length > 0)
}
